/**
 * Utility functions for handling currency symbols and formatting
 */

// List of Arabic/RTL currency symbols
export const ARABIC_CURRENCY_SYMBOLS = ['د.إ', '﷼', 'د.م.', 'د.ت', 'د.ج', 'ج.م'];

/**
 * Determines if a currency symbol is for a RTL language
 * @param {string} symbol - The currency symbol to check
 * @returns {boolean} - True if the symbol is for RTL text
 */
export const isRTLCurrency = (symbol) => {
  return ARABIC_CURRENCY_SYMBOLS.includes(symbol);
};

/**
 * Get formatted value with currency symbol in correct order based on language direction
 * @param {string|number} value - The value to format
 * @param {string} symbol - The currency symbol to use
 * @returns {string} - Formatted currency string with symbol in correct position
 */
export const formatCurrencyWithSymbol = (value, symbol = '$') => {
  // Ensure we have a valid value and symbol
  if (value === undefined || value === null) {
    value = '';
  }
  
  if (symbol === undefined || symbol === null) {
    symbol = '$';
  }
  
  // Format number if needed
  let formattedValue = value;
  if (typeof value === 'number') {
    formattedValue = value.toFixed(2);
  } else if (typeof value === 'string' && !isNaN(parseFloat(value))) {
    formattedValue = parseFloat(value).toFixed(2);
  }
  
  // Handle placement of symbol based on language direction
  const isArabic = isRTLCurrency(symbol);
  if (isArabic) {
    return `${formattedValue} ${symbol}`;
  } else {
    return `${symbol}${formattedValue}`;
  }
};

/**
 * Ensures a currency field has a symbol, applying a default if needed
 * @param {object} field - Form field definition
 * @returns {string} - The currency symbol to use
 */
export const ensureCurrencySymbol = (field) => {
  if (!field) return '$';
  return field.currencySymbol || '$';
};

/**
 * Get currency display properties for UI rendering
 * @param {string} symbol - The currency symbol
 * @returns {object} - Object with direction and other display properties
 */
export const getCurrencyDisplayProps = (symbol = '$') => {
  const isRTL = isRTLCurrency(symbol);
  
  return {
    dir: isRTL ? 'rtl' : 'ltr',
    startAdornment: !isRTL ? symbol : null,
    endAdornment: isRTL ? symbol : null
  };
};